import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Select,
  Table,
  Tag,
} from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Flex from "components/shared-components/Flex";
import utils from "utils";
import { useTranslation } from "react-i18next";
import { arrayRemove, arrayUnion, doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import { formatPhoneNumber, getUserStatus } from "../../../../functions/formatters";
import {
  FACEBOOK_USER_URL,
  INSTAGRAM_USER_URL,
  LINKED_IN_USER_URL,
  TIKTOK_USER_URL,
  USER_LANGUAGE_EN,
  USER_LANGUAGE_NL,
  USER_LANGUAGES,
  USER_STATUS_ACTIVE,
  USER_STATUS_INVITED,
  USER_STATUS_PENDING,
  USER_STATUS_REJECTED,
} from "../../../../constants/UserConstant";
import { FIRESTORE_CUSTOMERS_TABLE, FIRESTORE_USERS_TABLE } from "../../../../constants/FirestoreConstant";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";
import dayjs from "dayjs";
import styled from "styled-components";
import { changeUserPassword, getFirebaseIDToken } from "../../../../functions/firebase/firebase";
import { inviteUser } from "../../../../functions/api/mail";
import { isMobile } from "../../../../functions/browser";
import { COMPANY_CONSTANTS } from "../../../../constants/CompanyConstants";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../../constants/DateConstant";
import { getUserExcelExportData } from "../../../../functions/excel";
import axios from "axios";
import urls from "../../../../urls";
import ExcelExport from "../../../../components/shared-components/ExcelExport";
import { useCustomers } from "../../../../data/hooks/firestore/useCustomers";
import { useWebshopUsers } from "../../../../data/hooks/firestore/useWebshopUsers";
import Loading from "../../../../components/shared-components/Loading";
import TextArea from "antd/es/input/TextArea";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import roles from "../../../../roles";

const { Option } = Select;

const Users = () => {
  const { webshopUsers: users, isLoading: webshopUsersLoading } = useWebshopUsers();
  const { customers: companies, isLoading: companiesLoading } = useCustomers();
  const { email, profile } = useAuth();
  const [userStatusList, setUserStatusList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState(users);
  const [searchText, setSearchText] = useState("");
  const [userEditForm] = Form.useForm();
  const [editPasswordForm] = Form.useForm();
  const [addUserForm] = Form.useForm();
  const [userEditModalVisible, setUserEditModalVisible] = useState(false);
  const [editPasswordModalVisible, setEditPasswordModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(null);
  const [user, setUser] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    if (!!users) {
      setUserStatusList([USER_STATUS_PENDING, USER_STATUS_ACTIVE, USER_STATUS_REJECTED]);
      onSearch(searchText);
    }
  }, [users]);

  const showDrawer = (row) => {
    setDrawerVisible(true);
    setUser(row);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setUser(null);
  };

  const dropdownMenu = (row) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => showDrawer(row)}>
          <Flex alignItems="center">
            <EyeOutlined />
            <span style={{ marginLeft: 4 }}>{t("details")}</span>
          </Flex>
        </a>
      </Menu.Item>
      <Menu.Item onClick={() => onPressEditUser(row)}>
        <Flex alignItems="center">
          <EditOutlined />
          <span style={{ marginLeft: 4 }}>{t("edit")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item
        onClick={() => {
          setUserToEdit(row);
          setEditPasswordModalVisible(true);
        }}
      >
        <Flex alignItems="center">
          <EditOutlined />
          <span style={{ marginLeft: 4 }}>{t("edit_password")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item>
        <Popconfirm
          placement="bottom"
          title={t("confirm_delete_customer", { name: row.userFirstName + " " + row.userLastName })}
          onConfirm={() => handleDeleteUser(row)}
          okText={t("yes")}
          cancelText={t("no")}
        >
          <a onClick={(e) => e.preventDefault}>
            <DeleteOutlined />
            {t("delete")}
          </a>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const onPressEditUser = (user) => {
    userEditForm.setFieldsValue({
      userFirstName: user.userFirstName,
      userLastName: user.userLastName,
      userPhoneNumber: user.userPhoneNumber,
      userCustomerId: user.userCustomerId,
      userLanguage: user.userLanguage || USER_LANGUAGE_EN,
      linkedin: user.socialMediaAccounts?.linkedin || null,
      facebook: user.socialMediaAccounts?.facebook || null,
      instagram: user.socialMediaAccounts?.instagram || null,
      tiktok: user.socialMediaAccounts?.tiktok || null,
      internalNotes: !!user.internalNotes ? user.internalNotes.text : null,
    });

    setUserToEdit(user);
    setUserEditModalVisible(true);
  };

  const handleEditUser = async () => {
    userEditForm.validateFields().then(async (values) => {
      const customerObject = companies.find((item) => item.customerId === values.userCustomerId);
      if (!!customerObject) {
        const obj = {
          internalNotes: null,
          userFirstName: values.userFirstName,
          userLastName: values.userLastName,
          userCustomerId: values.userCustomerId,
          userCustomerName: customerObject.customerName,
          userPhoneNumber: formatPhoneNumber(values.userPhoneNumber),
          userLanguage: values.userLanguage,
          socialMediaAccounts: {
            linkedin: values.linkedin || null,
            facebook: values.facebook || null,
            instagram: values.instagram || null,
            tiktok: values.tiktok || null,
          },
          updatedAt: dayjs().unix(),
          updatedBy: email,
        };

        if (values.internalNotes) {
          if (!!userToEdit.internalNotes && userToEdit.internalNotes.text !== values.internalNotes)
            obj.internalNotes = {
              ...userToEdit.internalNotes,
              text: values.internalNotes,
              updatedAt: dayjs().unix(),
              updatedBy: email,
            };
          else {
            obj.internalNotes = { text: values.internalNotes, createdAt: dayjs().unix(), createdBy: email };
          }
        }

        setIsLoading(true);
        try {
          await updateDoc(doc(firestore, FIRESTORE_USERS_TABLE, userToEdit.id), obj);
          closeEditModal();
          closeDrawer();
          if (userToEdit.userCustomerId !== values.userCustomerId) {
            try {
              await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, userToEdit.userCustomerId), {
                customerUsers: arrayRemove(userToEdit.id),
                updatedAt: dayjs().unix(),
                updatedBy: email,
              });
              await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, values.userCustomerId), {
                customerUsers: arrayUnion(userToEdit.id),
                updatedAt: dayjs().unix(),
                updatedBy: email,
              });
            } catch (err) {
              console.error("Error updating customer users:", err);
              message.error("Error updating customer users");
            }
          }
        } catch (err) {
          console.error("Error updating user:", err);
          message.error("Error updating user");
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleDeleteUser = (user) => {
    setIsLoading(true);
    getFirebaseIDToken()
      .then((token) => {
        const config = {
          headers: { Authorization: token },
        };
        return axios.delete(`${urls.deleteUser}/${user.userId}`, config);
      })
      .then(() => {
        message.success(t("notifications.user_deleted_successfully"));
      })
      .catch((err) => {
        console.log(err);
        const errorMessage = err.response?.data?.message || err.message;
        message.error(errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditPassword = () => {
    editPasswordForm.validateFields().then((values) => {
      setIsLoading(true);
      getFirebaseIDToken()
        .then((token) => {
          const object = {
            userId: userToEdit.id,
            password: values.password,
          };
          changeUserPassword(object, token)
            .then(() => {
              setEditPasswordModalVisible(false);
              setIsLoading(false);
              editPasswordForm.resetFields();
              message.success(t("notifications.password_changed_successfully"));
            })
            .catch((err) => {
              console.log(err);
              message.error(err.response.data.message);
              setIsLoading(false);
            });
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    });
  };

  const handleAddUser = () => {
    addUserForm.validateFields().then((values) => {
      setIsLoading(true);
      const customerObject = companies.find((item) => item.customerId === values.userCustomerId);
      if (!!customerObject) {
        values.userPhoneNumber = formatPhoneNumber(values.userPhoneNumber);
        values.userEmail = values.userEmail.toLowerCase().trim();
        if (values.internalNotes) {
          values.internalNotes = { text: values.internalNotes, createdAt: dayjs().unix(), createdBy: email };
        }
        const password = values.password;
        const socialMediaAccounts = {
          linkedin: values.linkedin || null,
          facebook: values.facebook || null,
          instagram: values.instagram || null,
          tiktok: values.tiktok || null,
        };
        delete values.password;
        delete values.linkedin;
        delete values.facebook;
        delete values.instagram;
        delete values.tiktok;

        const user = {
          userRegion: "NL",
          userBanned: false,
          userCustomerId: customerObject.customerId,
          userCustomerName: customerObject.customerName,
          userEmailVerified: true,
          userFavorites: [],
          userFcmToken: "",
          userId: "",
          userLanguage: values.userLanguage || USER_LANGUAGE_NL,
          userReceivePushNotifications: false,
          userRole: "user",
          userStatus: "active",
          createdAt: dayjs().unix(),
          socialMediaAccounts,
          ...values,
        };
        const object = {
          user: user,
          password: password,
        };
        getFirebaseIDToken()
          .then((token) => {
            inviteUser(object, token, false)
              .then(() => {
                closeAddModal();
                message.success(t("notifications.user_added_successfully"));
              })
              .catch((err) => {
                console.log(err);
                message.error(err.response.data.message);
                setIsLoading(false);
              });
          })
          .catch((err) => {
            console.log(err);
            setIsLoading(false);
          });
      } else {
        setIsLoading(false);
        message.error(t("notifications.customer_not_found"));
      }
    });
  };

  const closeEditModal = () => {
    setUserToEdit(null);
    setUserEditModalVisible(false);
    userEditForm.resetFields();
  };

  const closeAddModal = () => {
    setAddUserModalOpen(false);
    setIsLoading(false);
    addUserForm.resetFields();
  };

  const exportMenu = (
    <Menu>
      <Menu.Item key={"1"}>
        <ExcelExport
          data={getUserExcelExportData(users)}
          fileName={COMPANY_CONSTANTS.WEBSITE_NAME + " gebruikers export " + dayjs().format(DATE_FORMAT_DD_MM_YYYY)}
        >
          {t("shop_users")}
        </ExcelExport>
      </Menu.Item>
    </Menu>
  );

  const getUserStatusColor = (status) => {
    if (status === USER_STATUS_PENDING || status === USER_STATUS_INVITED) return "orange";
    else if (status === USER_STATUS_REJECTED) return "red";
    else if (status === USER_STATUS_ACTIVE) return "green";
    else return "green";
  };

  const userStatusMenu = (user) => (
    <Menu>
      {userStatusList
        .filter((item) => item !== user.userStatus)
        .map((item, key) => {
          return (
            <Menu.Item key={key}>
              <Popconfirm
                title={
                  <p>
                    {t("customer.change_status.intro", { userEmail: user.userEmail })}{" "}
                    <b>
                      {t("customer.change_status.bold", {
                        status: getUserStatus(item),
                      })}
                    </b>
                    {t("customer.change_status.end")}
                  </p>
                }
                onConfirm={() => updateUserStatus(user, item)}
                okText={t("yes")}
                cancelText={t("no")}
              >
                <a onClick={(e) => e.preventDefault()}>{getUserStatus(item)}</a>
              </Popconfirm>
            </Menu.Item>
          );
        })}
    </Menu>
  );

  const updateUserStatus = async (user, status) => {
    const userStatusObject = {
      userStatus: status,
      userBanned: status === USER_STATUS_REJECTED,
      updatedAt: dayjs().unix(),
      updatedBy: email,
    };
    try {
      const userDocRef = doc(firestore, FIRESTORE_USERS_TABLE, user.userId);
      await updateDoc(userDocRef, userStatusObject);
      message.success(t("customer.status_changed_success"));
    } catch (error) {
      message.error(t("customer.status_changed_failure"));
    }
  };

  const tableColumns = [
    {
      title: t("user.user"),
      render: (_, data) => (
        <button className={"text-button"} onClick={() => showDrawer(data)}>
          <span>{!!data.userFirstName ? data.userFirstName + " " + data.userLastName : data.userId}</span>
        </button>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "userFirstName"),
      defaultSortOrder: "descend",
    },
    {
      title: t("company"),
      dataIndex: "userCustomerName",
      render: (data) => <span>{data}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "userCustomerName"),
    },
    {
      title: t("email"),
      dataIndex: "userEmail",
      render: (data) => <span>{data}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "userEmail"),
    },
    {
      title: t("phone"),
      dataIndex: "userPhoneNumber",
      render: (data) => <span>{data}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "userPhoneNumber"),
    },
    {
      title: t("customer.app_version"),
      dataIndex: "userAppVersion",
      render: (_, user) => <span> {!!user.userAppVersion ? user.userAppVersion : "-"}</span>,
    },
    {
      title: t("status"),
      dataIndex: "userStatus",
      render: (_, user) => (
        <>
          <Dropdown overlay={userStatusMenu(user)} trigger={["click"]}>
            <a onClick={(e) => e.preventDefault()}>
              <Tag color={getUserStatusColor(user.userStatus)}>{getUserStatus(user.userStatus)}</Tag>
            </a>
          </Dropdown>
        </>
      ),
    },
    {
      title: "",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const onSearch = (value) => {
    setSearchText(value);
    const data = utils.wildCardSearchOnUser(users, value);
    setFilteredUsers(data);
  };

  if (webshopUsersLoading) {
    return <Loading />;
  }

  return (
    <>
      <Row className={"mb-2"}>
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>{t("shop_users")}</h1>
        </Col>
        <Col xs={24}>
          <Card>
            <Flex alignItems="center" justifyContent="between" mobileFlex={false}>
              <Flex className="mb-1" mobileFlex={false}>
                <div className="mr-md-3 mb-3">
                  <Input
                    value={searchText}
                    placeholder={t("search")}
                    prefix={<SearchOutlined />}
                    onChange={(e) => onSearch(e.target.value)}
                  />
                </div>
              </Flex>
              <div className={"d-flex"} style={{ gap: 12 }}>
                <Dropdown overlay={exportMenu}>
                  <Button className="ant-btn-primary">
                    <FileExcelOutlined /> {t("export")} <DownOutlined />
                  </Button>
                </Dropdown>
                <Button type={"primary"} onClick={() => setAddUserModalOpen(true)}>
                  {t("add_user")}
                </Button>
              </div>
            </Flex>
            <div className="table-responsive">
              <Table
                showSorterTooltip={false}
                columns={tableColumns}
                dataSource={filteredUsers}
                rowKey="userId"
                scroll={{ x: 1300 }}
              />
            </div>
            {!!user && (
              <Drawer
                title={t("user_data", { user: !!user ? user.userFirstName + " " + user.userLastName : "-" })}
                placement="right"
                onClose={closeDrawer}
                open={drawerVisible}
                width={isMobile ? window.innerWidth : 850}
              >
                <Descriptions
                  bordered
                  title={<div className="flex-row">{t("user_data", { user: "" })}</div>}
                  size="small"
                  column={1}
                >
                  <Descriptions.Item label={t("user.user")}>
                    {user.userFirstName + " " + user.userLastName}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.id")}>
                    {profile?.userRole === roles.superAdmin ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const win = window.open("", "_blank");
                          win.document.write(`<pre>${JSON.stringify(user, null, 2)}</pre>`);
                          win.document.close();
                        }}
                        style={{ color: "rgba(0, 0, 0, 0.88)", cursor: "default" }}
                      >
                        {user.userId}
                      </a>
                    ) : (
                      user.userId
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.status")}>{getUserStatus(user.userStatus)}</Descriptions.Item>
                  <Descriptions.Item label={t("user.company")}>{user.userCustomerName}</Descriptions.Item>
                  <Descriptions.Item label={t("user.email")}>{user.userEmail}</Descriptions.Item>
                  <Descriptions.Item label={t("user.phone")}>{user.userPhoneNumber}</Descriptions.Item>
                  <Descriptions.Item label={t("region")}>
                    {t("user.region", { region: user.userRegion })}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("internal_notes")}>
                    {!!user.internalNotes ? (
                      <Popover
                        content={
                          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                            <li>
                              <strong>{t("by")}:</strong> {user.internalNotes.updatedBy || user.internalNotes.createdBy}
                            </li>
                            <li>
                              <strong>{t("date")}:</strong>{" "}
                              {dayjs
                                .unix(user.internalNotes.updatedAt || user.internalNotes.createdAt)
                                .format("DD-MM-YYYY, HH:mm")}
                            </li>
                          </ul>
                        }
                      >
                        {user.internalNotes.text}
                      </Popover>
                    ) : (
                      "-"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("social_media_accounts")}>
                    {user.socialMediaAccounts ? (
                      <>
                        {user.socialMediaAccounts.linkedin && (
                          <a
                            href={`https://${LINKED_IN_USER_URL}${user.socialMediaAccounts.linkedin}`}
                            target={"_blank"}
                            rel={"noreferrer noopener"}
                            style={{ display: "block" }}
                          >{`${LINKED_IN_USER_URL}${user.socialMediaAccounts.linkedin}`}</a>
                        )}
                        {user.socialMediaAccounts.facebook && (
                          <a
                            href={`https://${FACEBOOK_USER_URL}${user.socialMediaAccounts.facebook}`}
                            target={"_blank"}
                            rel={"noreferrer noopener"}
                            style={{ display: "block" }}
                          >{`${FACEBOOK_USER_URL}${user.socialMediaAccounts.facebook}`}</a>
                        )}
                        {user.socialMediaAccounts.instagram && (
                          <a
                            href={`https://${INSTAGRAM_USER_URL}${user.socialMediaAccounts.instagram}`}
                            target={"_blank"}
                            rel={"noreferrer noopener"}
                            style={{ display: "block" }}
                          >{`${INSTAGRAM_USER_URL}${user.socialMediaAccounts.instagram}`}</a>
                        )}
                        {user.socialMediaAccounts.tiktok && (
                          <a
                            href={`https://${TIKTOK_USER_URL}${user.socialMediaAccounts.tiktok}`}
                            target={"_blank"}
                            rel={"noreferrer noopener"}
                            style={{ display: "block" }}
                          >{`${TIKTOK_USER_URL}${user.socialMediaAccounts.tiktok}`}</a>
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </Descriptions.Item>
                </Descriptions>
                <ButtonsWrapper>
                  <Button onClick={() => onPressEditUser(user)}>{t("edit_data")}</Button>
                  {user.userStatus === "invited" && (
                    <div>
                      <Button
                        onClick={() => {
                          navigator.clipboard
                            .writeText(`https://shop.chefsfarm.nl/invitation/${user.userId}`)
                            .then(() => {
                              setCopied(true);
                              setTimeout(() => {
                                setCopied(false);
                              }, 1500); // Reset "copied" state after 1.5 seconds
                            });
                        }}
                      >
                        {t("copy_invitation_link")}
                      </Button>
                      {copied && <p style={{ color: "green" }}>{t("copied")}!</p>}
                    </div>
                  )}
                </ButtonsWrapper>
                <Descriptions
                  bordered
                  title={<div className="flex-row mt-4">{t("user.app_and_device_data")}</div>}
                  size="small"
                  column={1}
                >
                  <Descriptions.Item label={t("user.app_version")}>
                    {user.userAppVersion ? user.userAppVersion : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.device")}>
                    {user.userDevice ? user.userDevice + " (" + user.userDeviceVersion + ")" : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("verified")}>
                    {user.userEmailVerified ? t("yes") : t("no")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.receive_notifications")}>
                    {user.userReceivePushNotifications ? t("yes") : t("no")}
                  </Descriptions.Item>
                </Descriptions>

                <Descriptions
                  bordered
                  title={<div className="flex-row mt-4">{t("user.user_actions_data")}</div>}
                  size="small"
                  column={1}
                >
                  <Descriptions.Item label={t("user.registration_date")}>
                    {!!user.userRegistrationDate
                      ? dayjs(user.userRegistrationDate, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY HH:mm")
                      : "-"}
                  </Descriptions.Item>

                  <Descriptions.Item label={t("user.shopping_cart_items")}>
                    {user.userShoppingCartNrOfItems ? user.userShoppingCartNrOfItems : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.last_update")}>
                    {!!user.userLastUpdate
                      ? dayjs(user.userLastUpdate, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY HH:mm")
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.last_action")}>
                    {!!user.userLastAction ? user.userLastAction : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.last_order")}>
                    {!!user.userLastOrderDate
                      ? dayjs(user.userLastOrderDate, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY HH:mm")
                      : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Drawer>
            )}
            <Modal
              title={t("edit_user_data", {
                user: !!userToEdit ? userToEdit.userFirstName + " " + userToEdit.userLastName : "",
              })}
              open={userEditModalVisible}
              onOk={handleEditUser}
              onCancel={closeEditModal}
              okText={t("save")}
              cancelText={t("close")}
            >
              <Form layout="vertical" form={userEditForm} onFinish={handleEditUser}>
                <Row gutter={16}>
                  <Col lg={12}>
                    <Form.Item
                      name={"userFirstName"}
                      label={t("user.first_name")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_user_firstName"),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item
                      name={"userLastName"}
                      label={t("user.last_name")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_user_lastName"),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item
                      name={"userPhoneNumber"}
                      label={t("user.phone")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_phone"),
                        },
                        {
                          pattern: /^[+]?(?:[0-9][\s\-]*){10,15}$/,
                          message: t("form.enter_valid_phone"),
                        },
                        {
                          type: "tel",
                        },
                      ]}
                      tooltip={{ title: t("enter_phone_tooltip"), icon: <InfoCircleOutlined /> }}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item
                      name={"userCustomerId"}
                      label={t("user.company")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_customer_name"),
                        },
                      ]}
                      tooltip={{ title: t("select_customer_tooltip"), icon: <InfoCircleOutlined /> }}
                    >
                      <Select
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        placeholder={t("form.choose_company")}
                        loading={companiesLoading}
                      >
                        {!!companies &&
                          companies.map((company) => (
                            <Option key={company.customerId} value={company.customerId}>
                              {company.customerName}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item
                      name={"userLanguage"}
                      label={t("user.language")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_user_language"),
                        },
                      ]}
                    >
                      <Select placeholder={t("form.choose_language")}>
                        {USER_LANGUAGES.map((language) => (
                          <Option key={language.value} value={language.value}>
                            {language.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Divider style={{ marginTop: 0 }} />
                  <Col xs={24}>
                    <Form.Item name={"internalNotes"} label={t("internal_notes")}>
                      <TextArea placeholder={t("internal_user_notes_placeholder")} />
                    </Form.Item>
                  </Col>
                  <Col>{t("social_media_accounts")}</Col>
                  <Col xs={24}>
                    <Form.Item name={"linkedin"} style={{ marginBottom: 8 }}>
                      <Input addonBefore={LINKED_IN_USER_URL} size="small" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name={"facebook"} style={{ marginBottom: 8 }}>
                      <Input addonBefore={FACEBOOK_USER_URL} size="small" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name={"instagram"} style={{ marginBottom: 8 }}>
                      <Input addonBefore={INSTAGRAM_USER_URL} size="small" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name={"tiktok"} style={{ marginBottom: 8 }}>
                      <Input addonBefore={TIKTOK_USER_URL} size="small" />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Modal>
            <Modal
              title={t("add_user")}
              open={addUserModalOpen}
              onOk={handleAddUser}
              onCancel={closeAddModal}
              okText={t("save")}
              cancelText={t("close")}
              confirmLoading={isLoading}
            >
              <Form layout="vertical" form={addUserForm}>
                <Row gutter={16}>
                  <Col lg={12}>
                    <Form.Item
                      name={"userFirstName"}
                      label={t("user.first_name")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_user_firstName"),
                        },
                      ]}
                    >
                      <Input placeholder={t("user.first_name")} />
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item
                      name={"userLastName"}
                      label={t("user.last_name")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_user_lastName"),
                        },
                      ]}
                    >
                      <Input placeholder={t("user.last_name")} />
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item
                      name={"userEmail"}
                      label={t("user.email")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_email"),
                        },
                        {
                          type: "email",
                          message: t("form.enter_valid_email"),
                        },
                      ]}
                    >
                      <Input placeholder={t("user.email")} />
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item
                      name={"password"}
                      label={t("user.password")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_password"),
                        },
                        () => ({
                          validator(rule, value) {
                            return value.length >= 6 ? Promise.resolve() : Promise.reject(t("form.password_length"));
                          },
                        }),
                      ]}
                    >
                      <Input.Password placeholder={t("user.password")} />
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item
                      name={"userPhoneNumber"}
                      label={t("user.phone")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_phone"),
                        },
                        {
                          pattern: /^[+]?(?:[0-9][\s\-]*){10,15}$/,
                          message: t("form.enter_valid_phone"),
                        },
                        {
                          type: "tel",
                        },
                      ]}
                      tooltip={{ title: t("enter_phone_tooltip"), icon: <InfoCircleOutlined /> }}
                    >
                      <Input placeholder={t("user.phone")} />
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item
                      name={"userCustomerId"}
                      label={t("user.company")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_customer_name"),
                        },
                      ]}
                      tooltip={{ title: t("select_customer_tooltip"), icon: <InfoCircleOutlined /> }}
                    >
                      <Select showSearch optionFilterProp="children" allowClear placeholder={t("form.choose_company")}>
                        {!!companies &&
                          companies.map((company) => (
                            <Option key={company.customerId} value={company.customerId}>
                              {company.customerName}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item
                      name={"userLanguage"}
                      label={t("user.language")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_user_language"),
                        },
                      ]}
                      initialValue={USER_LANGUAGE_EN}
                    >
                      <Select placeholder={t("form.choose_language")}>
                        {USER_LANGUAGES.map((language) => (
                          <Option key={language.value} value={language.value}>
                            {language.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Divider style={{ marginTop: 0 }} />
                  <Col xs={24}>
                    <Form.Item name={"internalNotes"} label={t("internal_notes")}>
                      <TextArea placeholder={t("internal_user_notes_placeholder")} />
                    </Form.Item>
                  </Col>
                  <Col>{t("social_media_accounts")}</Col>
                  <Col xs={24}>
                    <Form.Item name={"linkedin"} style={{ marginBottom: 8 }}>
                      <Input addonBefore={LINKED_IN_USER_URL} size="small" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name={"facebook"} style={{ marginBottom: 8 }}>
                      <Input addonBefore={FACEBOOK_USER_URL} size="small" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name={"instagram"} style={{ marginBottom: 8 }}>
                      <Input addonBefore={INSTAGRAM_USER_URL} size="small" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name={"tiktok"} style={{ marginBottom: 8 }}>
                      <Input addonBefore={TIKTOK_USER_URL} size="small" />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Modal>
            <Modal
              title={t("edit_user_password", {
                user: !!userToEdit ? userToEdit.userFirstName + " " + userToEdit.userLastName : "",
              })}
              open={editPasswordModalVisible}
              onOk={handleEditPassword}
              onCancel={() => {
                setUserToEdit(null);
                setEditPasswordModalVisible(false);
                editPasswordForm.resetFields();
              }}
              okText={t("save")}
              confirmLoading={isLoading}
              cancelText={t("close")}
            >
              <Form layout="vertical" form={editPasswordForm} onFinish={handleEditPassword}>
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Item
                      name={"password"}
                      label={t("user.password")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_password"),
                        },
                        () => ({
                          validator(rule, value) {
                            return value.length >= 6 ? Promise.resolve() : Promise.reject(t("form.password_length"));
                          },
                        }),
                      ]}
                    >
                      <Input.Password placeholder={t("user.password")} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Modal>
          </Card>
        </Col>
      </Row>
    </>
  );
};

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

export default Users;
